import { useRouterState } from "@tanstack/react-router";
import {
  AlarmClock,
  ArrowRight,
  Bell,
  Check,
  ShieldCheck,
  X,
} from "lucide-react";
import { createElement, type ReactNode } from "react";

import { RaAvatar } from "@/components/ui";
import { cn } from "@/lib/cn";

import { ProvingLabel } from "@/pages/ai/ProvingBadge";

import { useAlertCenter } from "./center";
import { iconOf, toneOf, TONE, whenText, type AlertItem } from "./inbox";

/** The body text with its key figure in bold colour ("Paper result **+₹17,052** on 1 lot"). */
export function BodyText({
  item,
  dark = false,
}: {
  item: AlertItem;
  dark?: boolean;
}) {
  if (!item.body) return null;
  const fig = item.figure;
  if (!fig || !item.body.includes(fig.text)) return <>{item.body}</>;
  const at = item.body.indexOf(fig.text);
  const tone =
    fig.tone === "up"
      ? dark
        ? "text-emerald-400"
        : "text-up"
      : fig.tone === "down"
        ? dark
          ? "text-rose-400"
          : "text-down"
        : dark
          ? "text-white"
          : "text-fg";
  return (
    <>
      {item.body.slice(0, at)}
      <b className={cn("font-semibold", tone)}>{fig.text}</b>
      {item.body.slice(at + fig.text.length)}
    </>
  );
}

/** Market Radar's "Why is it moving?" (V100): the reason with its source and time,
 * or "No public news found yet". Filled a little after the card arrives. */
export function WhyLine({
  item,
  dark = false,
  className,
}: {
  item: AlertItem;
  dark?: boolean;
  className?: string;
}) {
  const why = item.why;
  if (!why) return null;
  return (
    <p
      className={cn(
        "mt-1 line-clamp-4 text-xs leading-snug break-words",
        why.found
          ? dark
            ? "text-white/85"
            : "text-fg"
          : dark
            ? "text-white/50"
            : "text-subtle",
        className,
      )}
    >
      {why.text}
    </p>
  );
}

export function KindIcon({
  item,
  className,
}: {
  item: AlertItem;
  className?: string;
}) {
  // an RA's call (V066): the RA's approved photo, else their initials
  if (item.ra_name) return <RaAvatar name={item.ra_name} photo={item.ra_photo} className={className} />;
  return (
    <span
      aria-hidden
      className={cn(
        "grid size-9 shrink-0 place-items-center rounded-lg [&_svg]:size-[18px]",
        TONE[toneOf(item)].tile,
        className,
      )}
    >
      {createElement(iconOf(item))}
    </span>
  );
}

function Chips({ item }: { item: AlertItem }) {
  if (!item.chips.length) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1">
      {item.chips.slice(0, 4).map((c) => (
        <span
          key={c.text}
          className={cn(
            "rounded-md px-1.5 py-px text-[11px] leading-4",
            c.tone === "down"
              ? "bg-rose-500/20 text-rose-200"
              : c.tone === "warn"
                ? "bg-amber-500/20 text-amber-200"
                : "bg-white/10 text-white/85",
          )}
        >
          <b className="font-semibold">{c.text}</b>
          {c.value ? (
            <span className="num ml-1 text-white/60">{c.value}</span>
          ) : null}
        </span>
      ))}
      {item.more || item.chips.length > 4 ? (
        <span className="self-center text-[11px] text-white/50">
          +{(item.more ?? 0) + Math.max(item.chips.length - 4, 0)} more
        </span>
      ) : null}
    </div>
  );
}

/** A small pill button on the dark card. */
function Pill({
  children,
  onClick,
  strong = false,
}: {
  children: ReactNode;
  onClick: () => void;
  strong?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium transition-colors [&_svg]:size-3.5 [&_svg]:shrink-0",
        strong
          ? "bg-white/90 text-slate-900 hover:bg-white"
          : "bg-white/10 text-white/90 hover:bg-white/20",
      )}
    >
      {children}
    </button>
  );
}

/** One alert as a compact notification, styled like iPhone and Mac notifications:
 * a dark frosted card with the kind and time, a bold title, two lines at most, and
 * small actions. The close button shows on hover (always on touch screens). */
export function AlertCard({
  item,
  preview = false,
}: {
  item: AlertItem;
  preview?: boolean;
}) {
  const center = useAlertCenter();
  const paper = item.paper;
  const team = item.kind === "announcement" || item.kind === "service";
  const act = (fn: () => void) => () => (preview ? undefined : fn());
  const main = item.link ? (
    <Pill strong onClick={act(() => center.open(item))}>
      <ArrowRight />
      <span className="truncate">{item.action}</span>
    </Pill>
  ) : null;
  const second = item.snooze ? (
    <Pill onClick={act(() => center.snooze(item))}>
      <AlarmClock />
      Snooze 1 h
    </Pill>
  ) : team ? (
    <Pill
      onClick={act(() =>
        item.link ? center.dismiss(item) : center.read([item.id]),
      )}
    >
      {item.link ? null : <Check />}
      {item.link ? "Later" : item.action || "OK"}
    </Pill>
  ) : (
    <Pill onClick={act(() => center.read([item.id]))}>
      <Check />
      Mark read
    </Pill>
  );
  return (
    <article
      aria-label={`${item.tag}: ${item.title}`}
      className="group relative rounded-2xl border border-white/10 bg-[rgb(30_30_34/0.88)] text-white shadow-[0_12px_32px_-8px_rgb(0_0_0/0.45)] ring-1 ring-black/20 backdrop-blur-xl backdrop-saturate-150"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={act(() => center.dismiss(item))}
        className="absolute -top-2 -left-2 grid size-5 place-items-center rounded-full border border-white/15 bg-[rgb(60_60_66)] text-white/80 shadow transition-opacity hover:text-white focus-visible:opacity-100 [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100"
      >
        <X className="size-3" />
      </button>
      <div className="flex gap-2.5 p-3">
        {item.ra_name ? (
          <RaAvatar name={item.ra_name} photo={item.ra_photo} size="sm" className="ring-white/20" />
        ) : (
          <span
            aria-hidden
            className={cn(
              "grid size-8 shrink-0 place-items-center rounded-lg text-white [&_svg]:size-4",
              TONE[toneOf(item)].stripe,
            )}
          >
            {createElement(iconOf(item))}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <p className="min-w-0 flex-1 truncate text-[11px] font-medium tracking-wide text-white/55 uppercase">
              {item.tag}
            </p>
            <span className="shrink-0 text-[11px] text-white/45">
              {preview ? "now" : whenText(item.created_at)}
            </span>
          </div>
          <h3 className="mt-0.5 line-clamp-2 text-[13.5px] leading-snug font-semibold break-words">
            {item.title}
          </h3>
          {item.body ? (
            <p className={cn("mt-0.5 text-[13px] leading-snug break-words text-white/75", item.body.includes("\n") ? "line-clamp-4 whitespace-pre-line" : "line-clamp-2")}>
              <BodyText item={item} dark />
            </p>
          ) : null}
          <WhyLine item={item} dark />
          <Chips item={item} />
          {paper ? (
            // the call's own proving status as a badge (rai.proving)
            <ProvingLabel label={item.paper_label} dark className="mt-1.5" />
          ) : null}
          {item.kind === "ra_call" && item.note ? (
            // the Research Analyst and their SEBI registration (V063)
            <p className="mt-1 flex items-center gap-1 text-[11px] text-white/55">
              <ShieldCheck aria-hidden className="size-3 shrink-0" />
              <span className="truncate">{item.note}</span>
            </p>
          ) : null}
          {item.friends_note ? (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-white/45">
              <ShieldCheck aria-hidden className="size-3 shrink-0" />
              <span className="truncate">{item.friends_note}</span>
            </p>
          ) : null}
          {main || second ? (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {main}
              {paper && !item.snooze ? null : second}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

/** The stack of cards at the top right, under the tickers, on every page. */
export function AlertCards() {
  const center = useAlertCenter();
  // the Alerts page lists every alert itself: no cards on top of it
  const onAlertsPage = useRouterState({
    select: (s) => s.location.pathname.startsWith("/app/alerts"),
  });
  if (
    !center.enabled ||
    onAlertsPage ||
    (!center.cards.length && !center.hidden)
  )
    return null;
  return (
    <section
      aria-label="New alerts"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-4 top-[calc(var(--shell-top,7rem)+0.75rem)] z-30 flex flex-col items-end gap-2 sm:inset-x-auto sm:right-5 sm:w-[22rem]"
    >
      {center.cards.map((c) => (
        <div
          key={c.id}
          className="pointer-events-auto w-full animate-[alert-in_220ms_ease-out]"
        >
          <AlertCard item={c} />
        </div>
      ))}
      {center.hidden ? (
        <button
          type="button"
          onClick={() => center.setBellOpen(true)}
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[rgb(30_30_34/0.88)] px-3 py-1.5 text-xs font-medium text-white/90 shadow-[0_8px_24px_-8px_rgb(0_0_0/0.45)] backdrop-blur-xl hover:bg-[rgb(44_44_50/0.92)]"
        >
          <Bell aria-hidden className="size-3.5" />
          {center.hidden} more in the bell
        </button>
      ) : null}
    </section>
  );
}
